
import React, { useState, useRef } from 'react';
import { Form, Button, Alert, Table } from 'react-bootstrap';
import { User } from 'firebase/auth';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import html2pdf from 'html2pdf.js';

interface EmiCalculatorProps {
  currentUser: User | null;
}

const EmiCalculator: React.FC<EmiCalculatorProps> = ({ currentUser }) => {
  const [principal, setPrincipal] = useState<number | string>('');
  const [rate, setRate] = useState<number | string>('');
  const [tenure, setTenure] = useState<number | string>('');
  const [emi, setEmi] = useState<number | null>(null);
  const [schedule, setSchedule] = useState<any[]>([]);
  const scheduleRef = useRef(null);

  const calculateEmi = () => {
    const p = Number(principal);
    const r = Number(rate) / 12 / 100;
    const n = Number(tenure) * 12;

    if (p > 0 && r > 0 && n > 0) {
      const emiValue = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      setEmi(emiValue);

      let balance = p;
      const newSchedule = [];
      for (let i = 0; i < n; i++) {
        const interest = balance * r;
        const principalPaid = emiValue - interest;
        balance -= principalPaid;
        newSchedule.push({
          month: i + 1,
          principal: principalPaid.toFixed(2),
          interest: interest.toFixed(2),
          balance: balance.toFixed(2),
        });
      }
      setSchedule(newSchedule);
    } else {
      setEmi(null);
      setSchedule([]);
    }
  };

  const downloadPdf = () => {
    const element = scheduleRef.current;
    if (element) {
      html2pdf().from(element).save();
    }
  };

  return (
    <div>
      <h3 className="mb-3">Loan/EMI Calculator</h3>
      <Form>
        <Form.Group className="mb-3">
          <Form.Label>Loan Amount (Principal)</Form.Label>
          <Form.Control
            type="number"
            value={principal}
            onChange={(e) => setPrincipal(e.target.value)}
            placeholder="Enter loan amount"
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Annual Interest Rate (%)</Form.Label>
          <Form.Control
            type="number"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="Enter annual interest rate"
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Loan Tenure (Years)</Form.Label>
          <Form.Control
            type="number"
            value={tenure}
            onChange={(e) => setTenure(e.target.value)}
            placeholder="Enter loan tenure in years"
          />
        </Form.Group>
        <Button variant="primary" onClick={calculateEmi}>
          Calculate EMI
        </Button>
        {currentUser && emi !== null && (
          <Button variant="secondary" onClick={async () => {
            try {
              await addDoc(collection(db, `users/${currentUser.uid}/savedCalculations`), {
                calculatorType: 'emi',
                principal,
                rate,
                tenure,
                emi,
                timestamp: new Date(),
              });
              alert('EMI calculation saved!');
            } catch (e) {
              console.error('Error saving document: ', e);
              alert('Error saving calculation.');
            }
          }} className="ms-2">
            Save Calculation
          </Button>
        )}
      </Form>
      {emi !== null && (
        <Alert variant="success" className="mt-4">
          <h5>Monthly EMI: {emi.toFixed(2)}</h5>
        </Alert>
      )}

      {schedule.length > 0 && (
        <div className="mt-4" ref={scheduleRef}>
          <h4>Repayment Schedule</h4>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Month</th>
                <th>Principal Paid</th>
                <th>Interest Paid</th>
                <th>Outstanding Balance</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((item) => (
                <tr key={item.month}>
                  <td>{item.month}</td>
                  <td>{item.principal}</td>
                  <td>{item.interest}</td>
                  <td>{item.balance}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {schedule.length > 0 && (
        <Button variant="info" onClick={downloadPdf} className="mt-3">
          Download as PDF
        </Button>
      )}
    </div>
  );
};

export default EmiCalculator;
